import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconInfoCircle, IconPencil, IconPlus, IconSparkles } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { MaintenanceTask } from '../../api/types';
import { useSession } from '../../app/session';
import { AttachmentList } from '../../components/Attachments';
import { formatDate } from '../../lib/format';
import { notifyError, notifySaved } from '../../lib/notify';
import { TaskFormModal } from './TaskFormModal';

/**
 * The aircraft's maintenance programme: the tasks and the intervals they run on.
 *
 * Populating from templates gives a club the standard 25/50/100-hour, annual and overhaul
 * checks in one action; anything club-specific is added by hand alongside them.
 */
export function ProgrammeDrawer({
  opened,
  onClose,
  programmeId,
  aircraftId,
}: {
  opened: boolean;
  onClose: () => void;
  programmeId: number;
  aircraftId: number;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [editingTask, setEditingTask] = useState<MaintenanceTask | null>(null);
  const [isTaskFormOpen, setTaskFormOpen] = useState(false);

  const programme = useQuery({
    queryKey: ['maintenance', 'programme', 'detail', programmeId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/maintenance/programmes/{id}/', {
          params: { path: { id: programmeId } },
        }),
      ),
    enabled: opened,
  });

  const populate = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/maintenance/programmes/{id}/populate/', {
          params: { path: { id: programmeId } },
          body: { use_system_templates: true },
        }),
      ),
    onSuccess: async () => {
      notifySaved(t('maintenance.populateProgramme'));
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      await queryClient.invalidateQueries({ queryKey: ['aircraft'] });
    },
    onError: (error: ApiError) => notifyError(error),
  });

  const tasksByArea = useMemo(() => {
    const groups = new Map<string, MaintenanceTask[]>();
    for (const task of programme.data?.tasks ?? []) {
      const area = task.area ?? 'AIRFRAME';
      const list = groups.get(area) ?? [];
      list.push(task);
      groups.set(area, list);
    }
    return Array.from(groups.entries());
  }, [programme.data?.tasks]);

  const canEdit = can('maintenance.manage_programme');

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Group gap="sm">
          <Title order={3}>{t('maintenance.programme')}</Title>
          {programme.data?.registration ? (
            <Badge className="identifier" variant="light">
              {programme.data.registration}
            </Badge>
          ) : null}
        </Group>
      }
    >
      {programme.isLoading ? (
        <Loader />
      ) : (
        <Stack gap="md">
          {programme.data?.reference || programme.data?.camo ? (
            <Group gap="lg">
              {programme.data.reference ? (
                <div>
                  <Text fz="xs" tt="uppercase" c="dimmed" fw={600}>
                    {t('organisation.approvalNumber')}
                  </Text>
                  <Text fz="sm">{programme.data.reference}</Text>
                </div>
              ) : null}
              {programme.data.camo ? (
                <div>
                  <Text fz="xs" tt="uppercase" c="dimmed" fw={600}>
                    {t('maintenance.camo')}
                  </Text>
                  <Text fz="sm">{programme.data.camo}</Text>
                </div>
              ) : null}
              {programme.data.approved_on ? (
                <div>
                  <Text fz="xs" tt="uppercase" c="dimmed" fw={600}>
                    {t('common.validFrom')}
                  </Text>
                  <Text fz="sm">{formatDate(programme.data.approved_on)}</Text>
                </div>
              ) : null}
            </Group>
          ) : null}

          {canEdit ? (
            <Group gap="xs">
              <Button
                variant="light"
                leftSection={<IconSparkles size={16} />}
                loading={populate.isPending}
                onClick={() => populate.mutate()}
              >
                {t('maintenance.populateProgramme')}
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setEditingTask(null);
                  setTaskFormOpen(true);
                }}
              >
                {t('maintenance.task')}
              </Button>
            </Group>
          ) : null}

          <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />} fz="sm">
            {t('maintenance.populateHint')}
          </Alert>

          {tasksByArea.map(([area, tasks]) => (
            <div key={area}>
              <Text fz="sm" fw={700} tt="uppercase" c="dimmed" mb={4}>
                {t(`maintenance.${area}`)}
              </Text>
              <Table fz="sm" withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('maintenance.task')}</Table.Th>
                    <Table.Th w={130}>{t('maintenance.interval')}</Table.Th>
                    <Table.Th w={120}>{t('maintenance.warnBefore')}</Table.Th>
                    <Table.Th w={40} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tasks.map((task) => (
                    <Table.Tr key={task.id} opacity={task.is_active === false ? 0.5 : 1}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text fz="sm" fw={500}>
                            {task.code}
                          </Text>
                          <Text fz="sm" truncate="end">
                            {task.name}
                          </Text>
                          {task.grounds_aircraft ? (
                            <Tooltip label={t('maintenance.groundsAircraft')}>
                              <Badge size="xs" color="red" variant="light">
                                AWY
                              </Badge>
                            </Tooltip>
                          ) : null}
                        </Group>
                        {task.reference ? (
                          <Text fz="xs" c="dimmed">
                            {task.reference}
                          </Text>
                        ) : null}
                      </Table.Td>
                      <Table.Td ff="monospace">{task.interval_display}</Table.Td>
                      <Table.Td>
                        <Text fz="xs" c="dimmed">
                          {[
                            task.warn_counter_before ? `${task.warn_counter_before}` : null,
                            task.warn_days_before ? `${task.warn_days_before} ${t('common.days')}` : null,
                          ]
                            .filter(Boolean)
                            .join(' / ') || '–'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {canEdit ? (
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => {
                              setEditingTask(task);
                              setTaskFormOpen(true);
                            }}
                          >
                            <IconPencil size={15} />
                          </ActionIcon>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          ))}

          <div>
            <Text fz="sm" fw={700} tt="uppercase" c="dimmed" mb={4}>
              {t('maintenance.attachments')}
            </Text>
            <AttachmentList
              target="maintenance.maintenanceprogramme"
              targetId={programmeId}
              canEdit={can('attachments.add')}
              compact
            />
          </div>
        </Stack>
      )}

      <TaskFormModal
        opened={isTaskFormOpen}
        onClose={() => setTaskFormOpen(false)}
        programmeId={programmeId}
        aircraftId={aircraftId}
        task={editingTask}
      />
    </Drawer>
  );
}
